import { useEffect, useMemo } from "react";
import { PackageCapability } from "#graphql/supergraphEnums";
import { usePackageDetailQuery } from "#graphql/supergraphHooks";
import { toIssueRows } from "../utils/checkDetail.utils";
import { isFayAuthFailure } from "../utils/fayAuthErrors";
import { PACKAGE_POLL_MS } from "./packagePolling";
import { useReportFayAuthFailure } from "./useFayAuthGate";
import type { PackageState } from "#graphql/supergraphTypes";
import type {
  DocumentSection,
  FootingComponentValue,
  PackageDocument,
  PackageEnumerationStatus,
  PackageExecutiveSummaryStatus,
} from "../viewModelTypes";
import type { ApolloError } from "@apollo/client";

/** Fixed order for package documents: Financial Statement first, PCAPs next,
 *  everything else (workbooks, other, unclassified) after. Applied once at the
 *  view-model layer so the Documents tab and Checks-tab accordions share one
 *  ordering, and the slideout's "first document" defaults (`documents[0]` and
 *  first-doc-with-checks) naturally pick the FS instead of whatever happened to
 *  upload first. */
const DOC_TYPE_ORDER: Record<string, number> = {
  financial_statement: 0,
  pcap: 1,
};
const documentSortRank = (documentType: string | null): number =>
  documentType !== null && DOC_TYPE_ORDER[documentType] !== undefined
    ? DOC_TYPE_ORDER[documentType]
    : 2;

/** Fay's synthetic bucket for scope-less checks (`DEFAULT_SECTION_NAME`). */
const GENERAL_SECTION_KEY = "General";

/**
 * Orders a document's sections for the Checks tab: synthetic `General` first,
 * then real sections ascending by `firstPage` (document order), then any other
 * page-less sections last. Stable via an index decoration so ties keep arrival
 * order.
 */
export function sortSectionsByDocumentOrder(sections: DocumentSection[]): DocumentSection[] {
  const rank = (section: DocumentSection): number => {
    if (section.key === GENERAL_SECTION_KEY) {
      return 0;
    }
    if (section.firstPage != null) {
      return 1;
    }
    return 2;
  };

  return sections
    .map((section, index) => ({ section, index }))
    .sort((a, b) => {
      const rankDiff = rank(a.section) - rank(b.section);
      if (rankDiff !== 0) {
        return rankDiff;
      }
      const pageA = a.section.firstPage;
      const pageB = b.section.firstPage;
      if (pageA != null && pageB != null && pageA !== pageB) {
        return pageA - pageB;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.section);
}

/** View-model the slideout renders; GraphQL-agnostic. */
export type PackageDetail = {
  id: string;
  entityName: string;
  reportingDate: string;
  /** Raw fay package type (e.g. `PeriodClose`); mapped to a label at render. */
  packageType: string;
  /** Whether the package is visible to clients, and when that last changed
   *  (null until the first publish). */
  publishState: PackageState;
  publishStateChangedAt: string | null;
  /** Whether the fund admin manages this package's entity. Publish state is
   *  meaningless otherwise, so the publish surfaces render nothing when false.
   *  Distinct from `actorCapabilities`: that is what *this viewer* may do, and
   *  a client on a managed entity holds no publish capability yet still needs
   *  the state. */
  isFaManaged: boolean;
  /** What this viewer may do to the package, narrowed to the capabilities this
   *  build knows. */
  actorCapabilities: readonly PackageCapability[];
  /** Number of current (non-superseded) finding envelopes, for the header stat
   *  count (the findings list itself sources rows from `triageQueue`). */
  findingCount: number;
  /** Whether checks are still enumerating; polled so the Checks tab can show a
   *  processing state and swap to results when COMPLETE. */
  enumerationStatus: PackageEnumerationStatus | null;
  /** Cached AI executive summary, or null when no generation has succeeded.
   *  `isStale` is queried for a future badge but not rendered in MVP. */
  executiveSummary: { text: string; generatedAt: string; isStale: boolean } | null;
  /** Lifecycle of the summary above; disambiguates a null `executiveSummary`
   *  (still generating vs. terminal failure). */
  executiveSummaryStatus: PackageExecutiveSummaryStatus | null;
  documents: PackageDocument[];
};

export type PackageDetailData = {
  package?: PackageDetail;
  loading: boolean;
  error?: ApolloError;
  /** The server explicitly returned no package — unknown id, a package in
   *  another arena, or a malformed uuid; fay returns the same empty result for
   *  all three deliberately, so they are indistinguishable here. */
  notFound: boolean;
  /** Re-runs the package read; the Checks tab calls it after a triage write so
   *  `triageState` and derived counts refresh from source. */
  refetch: () => void;
};

const KNOWN_CAPABILITIES = new Set<string>(Object.values(PackageCapability));

/**
 * Keeps the capabilities this build knows and drops the rest. Fay can add a
 * member to `PackageCapability` at any time, and an older client must keep
 * rendering: an unknown capability grants nothing here, so it reads the same as
 * a capability the viewer does not hold.
 */
function toPackageCapabilities(raw: readonly string[] | null | undefined): PackageCapability[] {
  return (raw ?? []).filter((value): value is PackageCapability =>
    KNOWN_CAPABILITIES.has(value),
  );
}

export function hasPackageCapability(
  capabilities: readonly PackageCapability[],
  capability: PackageCapability,
): boolean {
  return capabilities.includes(capability);
}

const MISSING_FILENAME = "Untitled document";

/**
 * Returns `value` when it carries a non-blank string, otherwise `fallback`.
 * Unlike `??`, this also treats empty/whitespace strings (which the backend can
 * return for an unset `name`/`originalFilename`) as missing.
 */
function withFallback(value: string | null | undefined, fallback: string): string {
  return value && value.trim() !== "" ? value : fallback;
}

/**
 * Normalizes the federated footing addends: returns null when the field is
 * absent (historical finding), otherwise sorts by (rowIndex, colIndex) and
 * coalesces a `"nan"` label — DDG's sentinel for a continuation cell — to the
 * most recent valid label so multi-line rows read as one addend. The carry
 * forward is seeded with `checkName` so a leading (or all-`"nan"`) label falls
 * back to the check name rather than rendering the literal `"nan"`, matching the
 * footing transformer the details table uses.
 */
export function normalizeComponentValues(
  raw: readonly FootingComponentValue[] | null | undefined,
  checkName: string,
): FootingComponentValue[] | null {
  if (raw == null) {
    return null;
  }
  if (raw.length === 0) {
    return null;
  }
  const sorted = [...raw].sort((a, b) =>
    a.rowIndex !== b.rowIndex ? a.rowIndex - b.rowIndex : a.colIndex - b.colIndex,
  );
  let lastValidLabel = checkName;
  return sorted.map((item) => {
    if (item.label !== "nan") {
      lastValidLabel = item.label;
    }
    return {
      label: item.label === "nan" ? lastValidLabel : item.label,
      value: item.value,
      rowIndex: item.rowIndex,
      colIndex: item.colIndex,
    };
  });
}

/**
 * Fetches a single deliverable package via fay's `package(id)` and maps it to a
 * flat view-model. `skip`s entirely when no package is selected so the closed
 * slideout issues no query. Findings are requested only to surface a header
 * count (the envelope id is enough); their severity/content still ride the
 * checks read, so the Checks tab itself stays gated on it.
 *
 * @param arenaId The read is arena-scoped server-side (fay requires arenaId);
 * undefined until the host app resolves its arena, which skips the query so the
 * required arg is never issued as NaN.
 */
export function usePackageDetail(
  packageId: string | null,
  arenaId: number | undefined,
): PackageDetailData {
  const { data, loading, error, refetch, stopPolling } = usePackageDetailQuery({
    variables: { arenaId: arenaId!, id: packageId ?? "" },
    skip: !packageId || arenaId === undefined,
    fetchPolicy: "cache-and-network",
    pollInterval: PACKAGE_POLL_MS,
    skipPollAttempt: () => document.hidden,
  });

  // A terminal fay auth failure (kill-switch/permission off while open, or a
  // logged-out session) won't recover, so stop polling and report up to the page
  // gate so the whole page swaps to a generic error page.
  const reportAuthFailure = useReportFayAuthFailure();
  useEffect(() => {
    if (error && isFayAuthFailure(error)) {
      stopPolling();
      reportAuthFailure(error);
    }
  }, [error, stopPolling, reportAuthFailure]);

  const pkg = useMemo<PackageDetail | undefined>(() => {
    const node = data?.package;
    if (!node) {
      return undefined;
    }
    return {
      id: node.id,
      entityName: withFallback(node.entity.name, node.entity.id),
      reportingDate: node.reportingDate,
      packageType: node.packageType,
      publishState: node.publishState,
      publishStateChangedAt: node.publishStateChangedAt ?? null,
      isFaManaged: node.entity.isFaLocked === true,
      actorCapabilities: toPackageCapabilities(node.actorCapabilities),
      findingCount: node.findings.length,
      enumerationStatus: node.enumerationStatus,
      executiveSummary: node.executiveSummary ?? null,
      executiveSummaryStatus: node.executiveSummaryStatus ?? null,
      documents: node.documents
        .map((doc) => ({
          id: doc.id,
          documentId: doc.documentId,
          documentVersion: doc.documentVersion,
          lifecycleStage: doc.lifecycleStage,
          gatingReason: doc.gating?.gatingReason ?? null,
          filename: withFallback(doc.metadata?.originalFilename, MISSING_FILENAME),
          documentType: doc.metadata?.documentType ?? null,
          uploadedAt: doc.metadata?.uploadedAt ?? null,
          uploadedByUserId: doc.metadata?.uploadedByUserId ?? null,
          sections: sortSectionsByDocumentOrder(
            doc.sections.map((section) => ({
              key: section.key,
              label: section.details?.label ?? null,
              firstPage: section.details?.firstPage ?? null,
              checks: section.checks.map((check) => {
                return {
                  checkId: check.checkId,
                  findingId: check.findingId ?? null,
                  checkName: check.checkName,
                  kind: check.kind,
                  source: check.source,
                  outcome: check.outcome,
                  severity: check.severity,
                  message: check.message ?? null,
                  expectedValue: check.expectedValue ?? null,
                  actualValue: check.actualValue ?? null,
                  componentValues: normalizeComponentValues(
                    check.componentValues,
                    check.checkName,
                  ),
                  rowLabel: check.rowLabel ?? null,
                  delta: check.delta ?? null,
                  issues: toIssueRows(check.issues),
                  triageState: check.triageState ?? null,
                  isOpen: check.isOpen,
                  feedback: check.feedback ?? null,
                };
              }),
            })),
          ),
        }))
        .sort((a, b) => documentSortRank(a.documentType) - documentSortRank(b.documentType)),
    };
  }, [data]);

  // Keyed off an explicitly received `package: null` rather than a missing
  // `pkg`: the host app resolves the arena asynchronously, so on a cold load the
  // query is still skipped (`loading` false, `data` undefined) and anything
  // derived from "no package object" would false-positive. It also excludes
  // auth failures, which leave `data` undefined under the default errorPolicy.
  const notFound =
    !!packageId &&
    arenaId !== undefined &&
    !loading &&
    !error &&
    data !== undefined &&
    data.package === null;

  return {
    package: pkg,
    loading,
    error,
    notFound,
    // Discard the query promise so the exposed refetch is a plain `() => void`;
    // callers just want to trigger a re-read, not await it.
    refetch: () => {
      refetch().catch(() => undefined);
    },
  };
}
